/**
 * Apufunktiot tilausnäkymille
 */

import { differenceInDays, format } from "date-fns";
import { getCustomerDisplayName } from "@/lib/formatters";
import type { OrderFilters, OrderListItem, OrderLineItem, OrderTabType } from "@/types/orders";
import type { PostgrestFilterBuilder } from "@supabase/postgrest-js";

type OrderStatus = "draft" | "confirmed" | "ready_for_installation" | "installed" | "completed" | "cancelled";
type OrderType = "new_work" | "pickup_work" | "field_work";

// ============ FILTERING HELPERS ============

/**
 * Tarkista onko aktiivisia suodattimia
 */
export function hasActiveFilters(filters: OrderFilters): boolean {
  return (
    filters.search !== "" ||
    filters.status !== "all" ||
    filters.orderType !== "all" ||
    filters.approval !== "all" ||
    filters.dateFrom !== undefined ||
    filters.dateTo !== undefined
  );
}

/**
 * Suodata tilaukset hakutermin perusteella
 * Hakee: tilausnumero, asiakas, hautausmaa, kumppani, konfiguraation nimi
 */
export function searchOrders(orders: OrderListItem[], searchTerm: string): OrderListItem[] {
  if (!searchTerm) return orders;
  
  const searchLower = searchTerm.toLowerCase().trim();
  if (!searchLower) return orders;

  return orders.filter((order) => {
    const customerName = getCustomerDisplayName(order.customers).toLowerCase();
    const cemeteryName = order.cemeteries?.name?.toLowerCase() || "";
    const partnerName = order.partners?.name?.toLowerCase() || "";

    // Hae myös konfiguraation nimellä
    const configLine = order.order_lines?.find(
      (line) => line.configurations?.product_models?.is_custom_template
    );
    const configName = configLine?.configurations?.name?.toLowerCase() || "";

    return (
      order.order_number.toLowerCase().includes(searchLower) ||
      customerName.includes(searchLower) ||
      cemeteryName.includes(searchLower) ||
      partnerName.includes(searchLower) ||
      configName.includes(searchLower)
    );
  });
}

/**
 * Laske suodatettujen tilausten määrä per tila
 */
export interface OrderStatusCounts {
  all: number;
  draft: number;
  confirmed: number;
  ready_for_installation: number;
  installed: number;
  completed: number;
  cancelled: number;
}

export function calculateOrderStatusCounts(orders: OrderListItem[]): OrderStatusCounts {
  const counts: OrderStatusCounts = {
    all: orders.length,
    draft: 0,
    confirmed: 0,
    ready_for_installation: 0,
    installed: 0,
    completed: 0,
    cancelled: 0,
  };

  orders.forEach((order) => {
    if (order.status in counts) {
      counts[order.status as keyof Omit<OrderStatusCounts, 'all'>]++;
    }
  });

  return counts;
}

// ============ CONFIGURATION HELPERS ============

export interface ConfigurationInfo {
  name: string | null;
  templateName: string;
  isCustom: boolean;
}

/**
 * Hae konfiguraation tiedot tilausriveiltä
 * Etsii ensimmäisen rivin jolla on custom template -konfiguraatio
 */
export function getConfigurationInfo(orderLines: OrderLineItem[]): ConfigurationInfo | null {
  for (const line of orderLines || []) {
    if (line.configurations?.product_models?.is_custom_template) {
      return {
        name: line.configurations.name,
        templateName: line.configurations.product_models.name,
        isCustom: true
      };
    }
  }
  return null;
}

/**
 * Hae ensimmäisen tilausrivin kuvaus
 */
export function getFirstProductDescription(orderLines: OrderLineItem[]): string | null {
  if (!orderLines || orderLines.length === 0) return null;
  return orderLines[0].description;
}

// ============ PROOF HELPERS ============

/**
 * Hae viimeisimmän vedoksen tila
 */
export function getLatestProofStatus(proofs: { id: string; status: string }[] | null): string | null {
  if (!proofs || proofs.length === 0) return null;
  return proofs[proofs.length - 1].status;
}

// ============ DATE HELPERS ============

export interface DaysBadgeInfo {
  days: number;
  colorClass: string;
}

/**
 * Laske tilauksen ikä ja palauta väriluokka
 * - >60 päivää: punainen (destructive)
 * - >30 päivää: oranssi
 * - >14 päivää: keltainen
 * - muuten: harmaa
 */
export function getDaysBadge(orderDate: string): DaysBadgeInfo {
  const days = differenceInDays(new Date(), new Date(orderDate));
  let colorClass = "bg-muted text-muted-foreground";
  if (days > 60) colorClass = "bg-destructive/10 text-destructive";
  else if (days > 30) colorClass = "bg-orange-500/10 text-orange-700";
  else if (days > 14) colorClass = "bg-yellow-500/10 text-yellow-700";
  return { days, colorClass };
}

// ============ LINK HELPERS ============

/**
 * Luo seurantalinkki tilauksen numerolla ja tokenilla
 * Käyttää encodeURIComponent yhtenäisyyden vuoksi Edge Function -puolen kanssa
 */
export function buildTrackingLink(orderNumber: string, trackingToken: string): string {
  return `${window.location.origin}/track/${encodeURIComponent(orderNumber)}/${encodeURIComponent(trackingToken)}`;
}

// ============ QUERY FILTER HELPERS ============

/**
 * Soveltaa tilaussuodattimet Supabase-kyselyyn
 * Käytetään sekä orders-taulun että order_search_view:n kanssa
 * Vähentää duplikaatiota useOrdersQuery hookissa
 * 
 * Huom: Käytetään `any` tyyppinä koska PostgrestFilterBuilder vaatii
 * monimutkaiset skeemasidonnaiset generics-parametrit
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function applyOrderFilters<T>(
  query: T,
  filters: OrderFilters,
  activeTab: OrderTabType
): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let q = query as any;

  // Tab filtering
  if (activeTab === "active") {
    q = q.is("deleted_at", null).not("status", "in", "(completed,cancelled)");
  } else if (activeTab === "archive") {
    q = q.is("deleted_at", null).in("status", ["completed", "cancelled"]);
  } else if (activeTab === "deleted") {
    q = q.not("deleted_at", "is", null);
  }

  // Status filter
  if (filters.status !== "all") {
    q = q.eq("status", filters.status as OrderStatus);
  }

  // Order type filter
  if (filters.orderType !== "all") {
    q = q.eq("order_type", filters.orderType as OrderType);
  }

  // Approval filter
  if (filters.approval !== "all") {
    if (filters.approval === "requires_approval") {
      q = q.eq("requires_cemetery_approval", true);
    } else {
      q = q.eq("cemetery_approval_status", filters.approval);
    }
  }

  // Date filters
  if (filters.dateFrom) {
    q = q.gte("order_date", format(filters.dateFrom, "yyyy-MM-dd"));
  }
  if (filters.dateTo) {
    q = q.lte("order_date", format(filters.dateTo, "yyyy-MM-dd"));
  }

  return q as T;
}
